import { Action, CardData, Connection, MapType, PostIt, Project } from '../types';
import { useProjectStore } from '../store/useProjectStore';
import { generateId } from './vsmGeometry';
import { isValidUuid } from './uuid';
import { supabase } from '../lib/supabaseClient';
import { normalizeActionsForProject, syncProjectActionsToSupabase } from '../services/actionSyncService';

/** Garante ID UUID para projetos locais/.wf legados — necessário para filtros cross-módulo. */
export function ensureProjectUuid(project: Project): { project: Project; idChanged: boolean } {
  if (isValidUuid(project.id)) {
    return { project, idChanged: false };
  }
  return { project: { ...project, id: crypto.randomUUID() }, idChanged: true };
}

/** Linha bruta da tabela `projects` no Supabase (mapa em `data` JSONB). */
export interface SupabaseProjectRow {
  id: string;
  nome_projeto?: string | null;
  name?: string | null;
  status?: string | null;
  description?: string | null;
  created_at?: string | null;
  organization_id?: string | null;
  flow_type?: string | null;
  data?: Project | null;
  [key: string]: unknown;
}

type MapTypeSource = {
  estado_atual?: Project['estado_atual'] | null;
  flow_type?: string | null;
  enabledModules?: Project['enabledModules'];
  nome_projeto?: string | null;
  name?: string | null;
};

/** Resolução única de mapType — lista, hidratação e abertura devem usar a mesma lógica. */
export function resolveProjectMapType(
  source: MapTypeSource,
  override?: MapType | null,
): MapType | null {
  if (override === 'FACTORY' || override === 'OFFICE') {
    return override;
  }

  // Coluna DB tem prioridade sobre JSONB cacheado (evita estado_atual.mapType desatualizado).
  const flowType = source.flow_type;
  if (flowType === 'Industrial' || flowType === 'Logística' || flowType === 'Logistica') {
    return 'FACTORY';
  }
  if (flowType === 'Office') {
    return 'OFFICE';
  }

  const fromState = source.estado_atual?.mapType;
  if (fromState === 'FACTORY' || fromState === 'OFFICE') {
    return fromState;
  }

  if (source.enabledModules?.vsm === true) return 'FACTORY';
  if (source.enabledModules?.vsm === false) return 'OFFICE';

  const title = source.nome_projeto ?? source.name ?? '';
  if (/factory|industrial|vsm|logística|logistica|cd/i.test(title)) {
    return 'FACTORY';
  }

  return null;
}

interface RawConnection {
  sourceId: string;
  targetId: string;
}

type SheetLike = NonNullable<Project['estado_atual']>['sheets'] extends (infer S)[] | undefined ? S : never;

/** Mescla colunas da linha com o JSONB `data` no formato `Project` do canvas. */
export function normalizeSupabaseProjectRow(row: SupabaseProjectRow): Project {
  const embedded = (row.data ?? {}) as Project;
  const nome =
    embedded.nome_projeto
    ?? row.nome_projeto
    ?? row.name
    ?? embedded.name
    ?? 'Projeto sem nome';

  return {
    ...embedded,
    id: row.id,
    nome_projeto: nome,
    name: embedded.name ?? row.name ?? nome,
    status: (embedded.status ?? row.status ?? 'draft') as Project['status'],
    data_criacao: embedded.data_criacao ?? row.created_at ?? new Date().toISOString(),
    ultimo_salvamento: embedded.ultimo_salvamento ?? row.created_at ?? new Date().toISOString(),
    estado_atual: embedded.estado_atual,
    estado_futuro: embedded.estado_futuro ?? null,
    activeState: embedded.activeState ?? 'atual',
    enabledModules: embedded.enabledModules,
    globalDemand: embedded.globalDemand,
    workTime: embedded.workTime,
    taktTime: embedded.taktTime,
    optimizationLayer: embedded.optimizationLayer,
    optimizationSession: embedded.optimizationSession,
    flow_type: row.flow_type ?? (embedded as { flow_type?: string | null }).flow_type ?? null,
  };
}

function buildConnectionsBySource(connections: RawConnection[]): Map<string, Connection[]> {
  const connsBySrc = new Map<string, Connection[]>();
  for (const conn of connections) {
    if (!conn?.sourceId || !conn?.targetId) continue;
    if (!connsBySrc.has(conn.sourceId)) connsBySrc.set(conn.sourceId, []);
    connsBySrc.get(conn.sourceId)!.push({
      id: generateId(),
      targetId: conn.targetId,
      sourceHandle: 'right',
    });
  }
  return connsBySrc;
}

function inferSequentialConnections(cards: Partial<CardData>[]): RawConnection[] {
  const sorted = [...cards]
    .filter((card) => card.id)
    .sort((a, b) => (a.posicao?.x ?? 0) - (b.posicao?.x ?? 0));
  const result: RawConnection[] = [];
  for (let i = 0; i < sorted.length - 1; i += 1) {
    result.push({ sourceId: sorted[i].id!, targetId: sorted[i + 1].id! });
  }
  return result;
}

function extractRawConnections(
  loadedState: NonNullable<Project['estado_atual']>,
  cards: Partial<CardData>[],
): RawConnection[] {
  const stateConnections = (loadedState as { connections?: RawConnection[] }).connections;
  if (Array.isArray(stateConnections) && stateConnections.length > 0) {
    return stateConnections;
  }

  const sheetConnections = (loadedState.sheets ?? [])
    .flatMap((sheet) => ((sheet as { connections?: RawConnection[] }).connections ?? []));
  if (sheetConnections.length > 0) {
    return sheetConnections;
  }

  const hasAnyOutgoing = cards.some((card) => (card.conexoes_saida?.length ?? 0) > 0);
  if (hasAnyOutgoing) return [];

  return inferSequentialConnections(cards);
}

function inferResources(card: Partial<CardData>): Array<{ role: string; quantity: number }> | undefined {
  const existing = (card as { resources?: Array<{ role?: string; quantity?: number }> }).resources;
  if (existing?.length) {
    return existing.map((resource) => ({
      role: resource.role ?? '',
      quantity: Number(resource.quantity) || 1,
    }));
  }

  const name = card.responsavel || card.quem;
  if (!name) return undefined;

  return [{ role: name, quantity: Number(card.pessoasSimultaneas) || 1 }];
}

function normalizeCardForMapType(
  card: Partial<CardData>,
  mapType: MapType,
  connsBySrc: Map<string, Connection[]>,
): CardData {
  const outgoing = card.conexoes_saida?.length
    ? card.conexoes_saida
    : (connsBySrc.get(card.id!) ?? []);

  const resources = inferResources(card);
  const normalized: CardData = {
    ...(card as CardData),
    id: card.id!,
    tipo: card.tipo!,
    atividade: card.atividade || card.nome_fluxo || '',
    posicao: card.posicao ?? { x: 100, y: 200 },
    status: card.status === 'removido' ? 'removido' : 'ativo',
    conexoes_saida: outgoing,
    sector: card.sector || (card as { setor?: string }).setor,
    responsavel: card.responsavel || card.quem,
    tp: card.tp,
    lt: card.lt,
    tt: card.tt,
    traUnit: card.traUnit,
    entrada: card.entrada,
    pessoasSimultaneas: card.pessoasSimultaneas,
    nome_fluxo: card.nome_fluxo,
    quem: card.quem,
  };

  if (resources) {
    (normalized as CardData & { resources: Array<{ role: string; quantity: number }> }).resources = resources;
    normalized.quem = resources.map((resource) => resource.role).filter(Boolean).join(', ');
    if (!normalized.pessoasSimultaneas) {
      normalized.pessoasSimultaneas = resources.reduce((total, resource) => total + resource.quantity, 0);
    }
  }

  if (mapType === 'FACTORY') {
    if (normalized.tipo === 'atividade' && !normalized.vsmIcon) {
      normalized.vsmIcon = 'processo';
    }
    normalized.layoutType = normalized.layoutType ?? 'classic';
    normalized.mapOrigin = normalized.mapOrigin ?? 'FACTORY';
  } else {
    normalized.mapOrigin = normalized.mapOrigin ?? 'OFFICE';
    if (!normalized.layoutType) {
      normalized.layoutType = 'default';
    }
  }

  return normalized;
}

export function normalizeSheetsForCanvas(
  sheets: SheetLike[],
  mapType: MapType,
  loadedState: NonNullable<Project['estado_atual']>,
): SheetLike[] {
  const allCards = sheets.flatMap((sheet) => sheet.cards ?? []);
  const rawConnections = extractRawConnections(loadedState, allCards);
  const connsBySrc = buildConnectionsBySource(rawConnections);

  return sheets.map((sheet) => ({
    ...sheet,
    cards: (sheet.cards ?? []).map((card) => normalizeCardForMapType(card, mapType, connsBySrc)),
  }));
}

/** Unifica sheets vazias com cards legados em estado_atual.cards. */
export function resolveLoadedSheets(loadedState: NonNullable<Project['estado_atual']>) {
  const fallbackSheetId = loadedState.activeSheetId || generateId();
  const legacyCards = loadedState.cards ?? [];
  const rawSheets = loadedState.sheets ?? [];

  let loadedSheets = rawSheets.length
    ? rawSheets.map((sheet) => ({
        ...sheet,
        cards: sheet.cards?.length ? sheet.cards : (sheet.id === (loadedState.activeSheetId ?? rawSheets[0]?.id) ? legacyCards : sheet.cards ?? []),
      }))
    : legacyCards.length
      ? [{ id: fallbackSheetId, name: 'MAPA 1', cards: legacyCards }]
      : [{ id: fallbackSheetId, name: 'MAPA 1', cards: [] as typeof legacyCards }];

  const totalSheetCards = loadedSheets.reduce((total, sheet) => total + (sheet.cards?.length ?? 0), 0);
  if (totalSheetCards === 0 && legacyCards.length > 0) {
    loadedSheets = [{ id: fallbackSheetId, name: 'MAPA 1', cards: legacyCards }];
  }

  const activeLoadedSheetId =
    loadedState.activeSheetId && loadedSheets.some((sheet) => sheet.id === loadedState.activeSheetId)
      ? loadedState.activeSheetId
      : loadedSheets[0]?.id;

  return { loadedSheets, activeLoadedSheetId };
}

type ModeSnapshots = {
  current: { cards: CardData[]; postIts: PostIt[] } | null;
  kaizen: { cards: CardData[]; postIts: PostIt[] } | null;
  future: { cards: CardData[]; postIts: PostIt[] } | null;
};

const EMPTY_MODE_SNAPSHOTS: ModeSnapshots = { current: null, kaizen: null, future: null };

/** Lê os três mapas do arquivo. Arquivo sem snapshots não herda o projeto que estava aberto. */
function readModeSnapshots(loadedState: NonNullable<Project['estado_atual']>): ModeSnapshots {
  const raw = (loadedState as { modeSnapshots?: Partial<ModeSnapshots> | null }).modeSnapshots
    ?? (loadedState as { _modeSnapshots?: Partial<ModeSnapshots> | null })._modeSnapshots;
  if (!raw || typeof raw !== 'object') return { ...EMPTY_MODE_SNAPSHOTS };
  return {
    current: raw.current ?? null,
    kaizen: raw.kaizen ?? null,
    future: raw.future ?? null,
  };
}

export function projectHasMapContent(project: Project): boolean {
  const estado = project.estado_atual;
  if (!estado) return false;
  const sheetCards = (estado.sheets ?? []).reduce(
    (total, sheet) => total + (sheet.cards?.length ?? 0),
    0,
  );
  const legacyCards = estado.cards?.length ?? 0;
  const postIts = estado.postIts?.length ?? 0;
  return sheetCards + legacyCards > 0 || postIts > 0;
}

interface HydrateProjectOptions {
  setPostIts: (postIts: PostIt[]) => void;
  setOptimizationLayer: (layer: unknown[]) => void;
  mapTypeOverride?: MapType | null;
}

function inferMapType(project: Project, loadedState: NonNullable<Project['estado_atual']>): MapType {
  return resolveProjectMapType({
    estado_atual: loadedState,
    flow_type: (project as { flow_type?: string | null }).flow_type,
    enabledModules: project.enabledModules,
    nome_projeto: project.nome_projeto,
    name: (project as { name?: string }).name,
  }) ?? 'OFFICE';
}

/** Carrega estado_atual do projeto no Zustand store (mesma lógica do import .wf). */
export function hydrateProjectIntoStore(
  project: Project,
  { setPostIts, setOptimizationLayer, mapTypeOverride }: HydrateProjectOptions,
): boolean {
  const loadedState = project.estado_atual ?? ({} as NonNullable<Project['estado_atual']>);
  const loadedCardCount =
    (loadedState.sheets ?? []).reduce((total, sheet) => total + (sheet.cards?.length ?? 0), 0)
    + (loadedState.cards?.length ?? 0);
  const loadedPostItCount = loadedState.postIts?.length ?? 0;
  const canLoadMap = loadedCardCount > 0 || loadedPostItCount > 0;
  if (!canLoadMap) return false;

  const { loadedSheets, activeLoadedSheetId } = resolveLoadedSheets(loadedState);
  const inferredMapType = resolveProjectMapType(
    {
      estado_atual: loadedState,
      flow_type: project.flow_type,
      enabledModules: project.enabledModules,
      nome_projeto: project.nome_projeto,
      name: (project as { name?: string }).name,
    },
    mapTypeOverride,
  ) ?? inferMapType(project, loadedState);
  const normalizedSheets = normalizeSheetsForCanvas(loadedSheets, inferredMapType, loadedState);

  const { setSheets, setMapType, recalculateFlowVolumes } = useProjectStore.getState();
  setMapType(inferredMapType);
  setSheets(normalizedSheets, activeLoadedSheetId);

  const demand = Number(loadedState.globalDemand ?? project.globalDemand);
  const workTime = Number(loadedState.workTime ?? project.workTime);
  if (demand && workTime) {
    useProjectStore.setState({
      globalDemand: demand,
      workTime,
      taktTime: Number(loadedState.taktTime ?? project.taktTime) || workTime / demand,
    });
  }

  setPostIts(loadedState.postIts ?? []);
  const normalizedActions = normalizeActionsForProject(loadedState.actions ?? [], project.id);
  useProjectStore.getState().mergeProjectActions(project.id, normalizedActions);
  const loadedExtras = project as {
    optimizationLayer?: unknown[];
    optimizationSession?: ReturnType<typeof useProjectStore.getState>['optimizationSession'];
  };
  setOptimizationLayer(loadedExtras.optimizationLayer ?? []);
  const modeSnapshots = readModeSnapshots(loadedState);
  const loadedWorkDays = Number((loadedState as { factoryWorkDays?: number }).factoryWorkDays);

  useProjectStore.setState({
    currentProjectId: project.id,
    stage: loadedState.stage ?? 'diagnosis',
    completedStages: loadedState.completedStages ?? [],
    viewMode: loadedState.viewMode ?? 'current_clean',
    isKaizenMode: Boolean(loadedState.isKaizenMode),
    _modeSnapshots: modeSnapshots,
    optimizationSession: loadedExtras.optimizationSession ?? null,
    hasFutureMap: Boolean(modeSnapshots.future) || Boolean(project.estado_futuro),
    journeyDeviationAlert: false,
    factoryWorkDays: Number.isFinite(loadedWorkDays) && loadedWorkDays > 0 ? loadedWorkDays : 20,
  } as Partial<ReturnType<typeof useProjectStore.getState>>);

  if (project.nome_projeto) {
    useProjectStore.getState().setCurrentMapDisplayName(project.nome_projeto);
  }

  recalculateFlowVolumes();
  return true;
}

export function buildHydratedProject(project: Project, mapTypeOverride?: MapType | null): Project {
  const loadedState = project.estado_atual ?? ({} as NonNullable<Project['estado_atual']>);
  const { loadedSheets, activeLoadedSheetId } = resolveLoadedSheets(loadedState);
  const inferredMapType = resolveProjectMapType(
    {
      estado_atual: loadedState,
      flow_type: project.flow_type,
      enabledModules: project.enabledModules,
      nome_projeto: project.nome_projeto,
      name: (project as { name?: string }).name,
    },
    mapTypeOverride,
  ) ?? inferMapType(project, loadedState);
  const normalizedSheets = normalizeSheetsForCanvas(loadedSheets, inferredMapType, loadedState);
  const activeCards = normalizedSheets.find((sheet) => sheet.id === activeLoadedSheetId)?.cards ?? [];
  const normalizedActions = normalizeActionsForProject(loadedState.actions ?? [], project.id);

  return {
    ...project,
    estado_atual: {
      ...loadedState,
      mapType: inferredMapType,
      sheets: normalizedSheets,
      activeSheetId: activeLoadedSheetId,
      cards: activeCards,
      postIts: loadedState.postIts ?? [],
      actions: normalizedActions,
    },
  };
}

function mapCanvasStatusToDb(status?: Project['status']): string {
  if (status === 'master') return 'active';
  return status ?? 'draft';
}

export function mapTypeToFlowType(mapType: MapType): string {
  return mapType === 'FACTORY' ? 'Industrial' : 'Office';
}

/** Persiste mapa VSM/Office na tabela `projects` (JSONB `data`) da organização. */
export async function persistMapProjectToSupabase(
  project: Project,
  organizationId: string,
  userId: string,
  mapType: MapType,
  actionsOverride?: Action[],
): Promise<{ error: Error | null; actionsSynced: number }> {
  if (!isValidUuid(project.id)) {
    return { error: new Error('ID de projeto inválido para sincronização na nuvem.'), actionsSynced: 0 };
  }

  const projectName = project.nome_projeto?.trim() || 'Projeto sem nome';
  const savedAt = new Date().toISOString();
  const normalizedActions = normalizeActionsForProject(
    actionsOverride ?? project.estado_atual?.actions ?? [],
    project.id,
  );
  const projectForCloud: Project = {
    ...project,
    nome_projeto: projectName,
    name: projectName,
    ultimo_salvamento: savedAt,
    estado_atual: {
      ...(project.estado_atual ?? {} as NonNullable<Project['estado_atual']>),
      actions: normalizedActions,
      mapType,
    },
  };

  // Não enviar `updated_at` — coluna ausente no schema Brasil Flow (timestamp fica em data.ultimo_salvamento).
  const payload = {
    name: projectName,
    nome_projeto: projectName,
    description: (project as { description?: string | null }).description ?? null,
    status: mapCanvasStatusToDb(project.status),
    data: {
      ...projectForCloud,
      nome_projeto: projectName,
      name: projectName,
      flow_type: mapTypeToFlowType(mapType),
      ultimo_salvamento: savedAt,
    },
    flow_type: mapTypeToFlowType(mapType),
    project_type: 'vsm',
    visibility_scope: 'organization',
    visibility: 'published',
    organization_id: organizationId,
    owner_id: userId,
    created_by: userId,
    user_id: userId,
  };

  const { data: existing, error: existingError } = await supabase
    .from('projects')
    .select('id')
    .eq('id', project.id)
    .maybeSingle();

  if (existingError) {
    console.warn('[persistMapProject] Falha ao localizar o mapa:', existingError.message);
  }

  const writeExisting = async () => {
    const { data: updated, error } = await supabase
      .from('projects')
      .update(payload)
      .eq('id', project.id)
      .select('id');
    if (error) return { error: new Error(error.message), actionsSynced: 0 };
    if (!updated?.length) {
      return {
        error: new Error('Este mapa já existe, mas a nuvem não permitiu atualizá-lo.'),
        actionsSynced: 0,
      };
    }
    const actionsResult = await syncProjectActionsToSupabase(
      normalizedActions,
      organizationId,
      project.id,
      userId,
    );
    if (actionsResult.error) {
      console.warn('[persistMapProject] Mapa gravado; ações não sincronizadas:', actionsResult.error.message);
      return { error: null, actionsSynced: 0 };
    }
    return { error: null, actionsSynced: actionsResult.synced };
  };

  if (existing) {
    return writeExisting();
  }

  const { error } = await supabase.from('projects').insert({
    id: project.id,
    ...payload,
    created_at: project.data_criacao ?? new Date().toISOString(),
    progress: 0,
  });

  if (error) {
    const duplicate = error.code === '23505' || /duplicate key|projects_pkey/i.test(error.message ?? '');
    if (duplicate) return writeExisting();
    return { error: new Error(error.message), actionsSynced: 0 };
  }

  try {
    await supabase.from('project_members').upsert(
      { project_id: project.id, user_id: userId, role: 'owner' },
      { onConflict: 'project_id,user_id' },
    );
  } catch {
    // Tabela opcional — não bloqueia salvamento do mapa.
  }

  const actionsResult = await syncProjectActionsToSupabase(
    normalizedActions,
    organizationId,
    project.id,
    userId,
  );
  if (actionsResult.error) {
    console.warn('[persistMapProject] Mapa gravado; ações não sincronizadas:', actionsResult.error.message);
    return { error: null, actionsSynced: 0 };
  }

  return { error: null, actionsSynced: actionsResult.synced };
}
